/* Entry point for the field app, the page field.html loads (`27` section 2).
 *
 * Its routes are the web app's sign-in routes and the app behind the same guard
 * (`27` section 5): `/` signs in, or with no network goes on to `/app`
 * (NeedsSignal.tsx), `/auth/callback` is Entra's redirect URI, and
 * `/app` is the field app behind SessionGate. SignIn and AuthCallback send a
 * signed-in user to `/app`, so the field app is there too. The gate shows it
 * without a session as well, on the work the phone holds (`27` section 6), and
 * the manifest's start_url is `/app` so the installed app opens there.
 *
 * The query string the page was opened with selects what the app opens on.
 * It is read once, at load, because the routes through sign-in drop it:
 *   ?frame=1                 a desktop preview inside an iPhone bezel
 *   ?mode=capture | audit    skips the mode-select screen
 *   ?view=list | map | table the Audit queue's first view
 *
 * Not under StrictMode: its development double-mount constructs and removes
 * each MapLibre map twice, and the app's effects were written without it.
 *
 * AppBoundary wraps everything, as it does in main.tsx, so a throw leaves a
 * stated failure and a reload rather than a blank page
 * (components/ErrorBoundary.tsx).
 */
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SignIn from "../routes/SignIn";
import AuthCallback from "../routes/AuthCallback";
import { SessionGate } from "../shell/SessionGate";
import { AppBoundary } from "../components/ErrorBoundary";
import FieldApp from "./FieldApp";
import { NeedsSignal } from "./NeedsSignal";
import "../tokens.css";
import "../components.css";
import "./field.css";

const queryClient = new QueryClient();

/* The service worker (`27` section 6), registered at / so it controls every
 * page of its hostname - which is why only the mobile hostname registers it.
 * There nginx serves this page for every path; on the app hostname it is
 * reached only as /field.html, and a worker registered from there would serve
 * the web app too. Built only: the dev server has no /field-sw.js. */
if (import.meta.env.PROD && window.location.pathname !== "/field.html" && "serviceWorker" in navigator) {
  navigator.serviceWorker.register("/field-sw.js");
}

const opened = new URLSearchParams(window.location.search);

function Field() {
  return (
    <SessionGate local>
      <FieldApp
        frame={opened.get("frame") === "1"}
        startMode={opened.get("mode") || ""}
        defaultView={opened.get("view") || "list"}
      />
    </SessionGate>
  );
}

createRoot(document.getElementById("root")!).render(
  <AppBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<NeedsSignal><SignIn /></NeedsSignal>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/app" element={<Field />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </AppBoundary>,
);
